//#region types
import {
  AgentCaseSummary,
  AgentCaseSummaryRequest,
  AgentMessageRequest,
  AgentReply,
  AgentSessionCreated,
} from "models/agent/agentContract";
import { AuthEnums } from "models/enums";
//#endregion
//#region components
import useAPIClientHelper from "./useAPIClientHelper";
//#endregion

export class AgentServiceError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Superkoll's agent controller. "SupportAgent" because "Agent" there is the sales agent. */
const SessionsRoute = "api/SupportAgent/Sessions";

// The agent's routes through Superkoll, which authenticates her and forwards her own token. Raw
// posts typed by agentContract.ts: the generated client's AgentReply is an empty interface.
// skipUndefinedConversion is required - the default interceptor turns nulls into undefined, which
// would make a read-only card (confirmToken: null) confirmable.
const useAgentService = () => {
  const { apiClientInstance } = useAPIClientHelper();

  const post = async <T>(path: string, body: object): Promise<T> => {
    const client = apiClientInstance(AuthEnums.AuthType.Company, { skipUndefinedConversion: true });

    try {
      const response = await client.post(path, body);
      return response.data as T;
    } catch (error) {
      const { status, message } = (error ?? {}) as { status?: number; message?: string };
      throw new AgentServiceError(typeof status === "number" ? status : 0, message ?? `Agent call failed: ${path}`);
    }
  };

  const createSession = () => post<AgentSessionCreated>(SessionsRoute, {});

  const sendMessage = (sessionId: string, text: string, hasAttachments: boolean) => {
    const request: AgentMessageRequest = { text, hasAttachments };
    return post<AgentReply>(`${SessionsRoute}/${sessionId}/Messages`, request);
  };

  const summarizeCase = (sessionId: string, request: AgentCaseSummaryRequest) =>
    post<AgentCaseSummary>(`${SessionsRoute}/${sessionId}/CaseSummary`, request);

  return { createSession, sendMessage, summarizeCase };
};

export default useAgentService;
